import React, { useMemo, useState } from 'react';

import { AlineChip } from 'src/components';
import type { BoardLocalState } from 'src/components/workflow/board/Board';
import { FilterPopoverContent } from 'src/components/workflow/workflow-list/filter-contents/FilterPopoverContent';
import { useGetUserLabels } from 'src/hooks';
import type { ReactSetStateTuple } from 'src/types';

interface BoardLabelsFilterContentProps {
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
}

export function BoardLabelsFilterContent({
  boardStateTuple,
}: BoardLabelsFilterContentProps): JSX.Element {
  const [boardState, setBoardState] = boardStateTuple;
  const { labels } = useGetUserLabels();
  const [searchTerm, setSearchTerm] = useState('');

  const options = useMemo(
    () => labels.map(label => ({ id: label.id, label: label.labelText })),
    [labels]
  );

  const selectedIds = useMemo(
    () => new Set(boardState.filterLabels.map(l => l.id)),
    [boardState.filterLabels]
  );

  const handleToggle = (labelId: string) => {
    const label = labels.find(l => l.id === labelId);
    if (!label) return;

    const isSelected = selectedIds.has(labelId);
    const newLabels = isSelected
      ? boardState.filterLabels.filter(l => l.id !== labelId)
      : [
          ...boardState.filterLabels,
          { __typename: 'Label' as const, id: label.id, labelText: label.labelText },
        ];

    setBoardState(s => ({ ...s, filterLabels: newLabels }));
  };

  return (
    <FilterPopoverContent
      showSearch
      options={options}
      renderOption={option => <AlineChip chipText={option.label} variant="label" />}
      searchPlaceholder="Search labels..."
      searchTerm={searchTerm}
      selectedIds={selectedIds}
      onSearchChange={setSearchTerm}
      onToggle={handleToggle}
    />
  );
}
